import { useEffect, useState } from "react";

import { Plus, Trash2 } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useDiscountWrites, usePriceMetrics, type DiscountRow } from "../api";
import { DISCOUNT_TYPES } from "../catalog";
import { Field, writeError } from "./editorCommon";

interface GrantRow {
  _id: string;
  price_metric: string | null;
  quantity: number | null;
  recurrence: string;
}

let _seq = 0;
const dateOnly = (s: string | null | undefined) => (s ? s.slice(0, 10) : "");

/** Create/edit a billing discount. Type drives which fields apply. Note: the
 *  applicable-items / applicable-packages scoping arrays aren't editable here yet
 *  (PATCH preserves whatever Cinch already has). */
export function DiscountEditor({
  open,
  discount,
  onClose,
}: {
  open: boolean;
  discount: DiscountRow | null;
  onClose: () => void;
}) {
  const { save } = useDiscountWrites();
  const metricsQ = usePriceMetrics({ limit: 500, offset: 0 });
  const metricOptions: ComboOption[] = (metricsQ.data?.results ?? []).map((m) => ({
    id: m.id,
    label: m.name || m.id,
  }));

  const [name, setName] = useState("");
  const [type, setType] = useState("percentage");
  const [value, setValue] = useState<number | null>(null);
  const [code, setCode] = useState("");
  const [stackable, setStackable] = useState(false);
  const [description, setDescription] = useState("");
  const [custDescr, setCustDescr] = useState("");
  const [minQty, setMinQty] = useState<number | null>(null);
  const [maxQty, setMaxQty] = useState<number | null>(null);
  const [durationPeriods, setDurationPeriods] = useState<number | null>(null);
  const [periodUnit, setPeriodUnit] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [grants, setGrants] = useState<GrantRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(discount?.name ?? "");
    setType(discount?.type ?? "percentage");
    setValue(discount?.value ?? null);
    setCode(discount?.code ?? "");
    setStackable(!!discount?.stackable);
    setDescription(discount?.description ?? "");
    setCustDescr(discount?.customer_facing_descr ?? "");
    setMinQty(discount?.min_quantity ?? null);
    setMaxQty(discount?.max_quantity ?? null);
    setDurationPeriods(discount?.duration_periods ?? null);
    setPeriodUnit(discount?.period_unit ?? "month");
    setStartDate(dateOnly(discount?.start_date));
    setEndDate(dateOnly(discount?.end_date));
    setGrants(
      (discount?.unit_grants ?? []).map((g) => ({
        _id: `g${_seq++}`,
        price_metric: g.price_metric ?? null,
        quantity: g.quantity ?? null,
        recurrence: g.recurrence ?? "monthly",
      })),
    );
    save.reset();
  }, [open, discount]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchGrant = (id: string, patch: Partial<GrantRow>) =>
    setGrants((gs) => gs.map((g) => (g._id === id ? { ...g, ...patch } : g)));

  const isQty = type === "quantity_based";
  const isTime = type === "time_based";
  const isFixed = type === "fixed";

  const submit = () => {
    const body: Record<string, unknown> = {
      name: name.trim() || null,
      type,
      value: isQty ? null : value,
      code: code.trim() || null,
      stackable,
      description: description.trim() || null,
      customer_facing_descr: custDescr.trim() || null,
      min_quantity: isQty ? minQty : null,
      max_quantity: isQty ? maxQty : null,
      duration_periods: isTime ? durationPeriods : null,
      period_unit: periodUnit,
      start_date: isTime && startDate ? startDate : null,
      end_date: isTime && endDate ? endDate : null,
      unit_grants: grants
        .filter((g) => g.price_metric)
        .map((g) => ({ price_metric: g.price_metric, quantity: g.quantity, recurrence: g.recurrence })),
    };
    save.mutate({ id: discount?.id ?? null, body }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{discount ? "Edit discount" : "New discount"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" className="col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Discount name" />
          </Field>
          <Field label="Type">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISCOUNT_TYPES.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {!isQty && (
            <Field label={isFixed ? "Amount" : "Percent (%)"}>
              <NumberInput value={value} onChange={setValue} prefix={isFixed ? "$" : undefined} />
            </Field>
          )}
          <Field label="Code">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Stackable">
            <div className="flex h-9 items-center">
              <Switch checked={stackable} onCheckedChange={setStackable} />
            </div>
          </Field>

          {isQty && (
            <>
              <Field label="Min Quantity">
                <NumberInput value={minQty} onChange={setMinQty} />
              </Field>
              <Field label="Max Quantity">
                <NumberInput value={maxQty} onChange={setMaxQty} />
              </Field>
            </>
          )}

          {isTime && (
            <>
              <Field label="Duration">
                <NumberInput value={durationPeriods} onChange={setDurationPeriods} />
              </Field>
              <Field label="Period Unit">
                <Select value={periodUnit} onValueChange={setPeriodUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Start Date">
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Field>
              <Field label="End Date">
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Field>
            </>
          )}

          <Field label="Internal Description" className="col-span-2">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </Field>
          <Field label="Customer-facing Description" className="col-span-2">
            <Textarea value={custDescr} onChange={(e) => setCustDescr(e.target.value)} rows={2} />
          </Field>
        </div>

        {/* Unit grants sub-builder (free units per metric) */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Unit Grants
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setGrants((gs) => [
                  ...gs,
                  { _id: `g${_seq++}`, price_metric: null, quantity: null, recurrence: "monthly" },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add grant
            </Button>
          </div>
          {grants.length === 0 ? (
            <p className="text-sm text-muted-foreground">No unit grants.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {grants.map((g) => (
                <div key={g._id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2">
                  <Combobox
                    options={metricOptions}
                    value={g.price_metric}
                    onChange={(v) => patchGrant(g._id, { price_metric: v })}
                    placeholder="Price metric…"
                  />
                  <div className="w-28">
                    <NumberInput
                      value={g.quantity}
                      onChange={(v) => patchGrant(g._id, { quantity: v })}
                      placeholder="Quantity"
                    />
                  </div>
                  <div className="w-32">
                    <Select value={g.recurrence} onValueChange={(v) => patchGrant(g._id, { recurrence: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="one_time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove"
                    onClick={() => setGrants((gs) => gs.filter((x) => x._id !== g._id))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {save.error && (
          <Alert variant="danger">
            <AlertDescription>{writeError(save.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || !name.trim()}>
            {save.isPending ? "Saving…" : discount ? "Save changes" : "Create discount"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
